/**
 * Presentational status badge for a trustee's identity verification (KYC).
 * Pure view — status text/colour only, no data fetching.
 */
import {
  BadgeCheck,
  Clock,
  ShieldAlert,
  ShieldQuestion,
  XCircle,
} from "lucide-react";
import type { IdentityStatus } from "../hooks/use-identity-verification";

const CONFIG: Record<
  IdentityStatus,
  { label: string; className: string; Icon: typeof BadgeCheck }
> = {
  verified: {
    label: "Identity confirmed",
    className: "border-emerald-500/20 bg-emerald-500/10 text-emerald-500",
    Icon: BadgeCheck,
  },
  pending: {
    label: "Check in progress",
    className: "border-amber-500/20 bg-amber-500/10 text-amber-500",
    Icon: Clock,
  },
  failed: {
    label: "Check unsuccessful",
    className: "border-red-500/20 bg-red-500/10 text-red-500",
    Icon: XCircle,
  },
  expired: {
    label: "Check expired",
    className: "border-red-500/20 bg-red-500/10 text-red-500",
    Icon: ShieldAlert,
  },
  none: {
    label: "Identity not confirmed",
    className: "border-border bg-muted text-muted-foreground",
    Icon: ShieldQuestion,
  },
  unknown: {
    label: "Checking…",
    className: "border-border bg-muted text-muted-foreground",
    Icon: Clock,
  },
};

export function IdentityVerificationBadge({
  status,
}: {
  status: IdentityStatus;
}) {
  const { label, className, Icon } = CONFIG[status] ?? CONFIG.unknown;
  return (
    <span
      className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium ${className}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
